import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit6Notes: UnitNotes = {
  unitId: "unit-6",
  unitName: "Unit 6: Consequences of Industrialization (c. 1750 – c. 1900)",
  examWeight: "8–10%",
  focus: "How did industrialized nations use their technological and economic advantages to build global empires, and how did indigenous peoples respond?",
  sections: [
    {
      id: "topic-6-1",
      title: "Topic 6.1: Rationales for Imperialism",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 Cultural & Racial Ideologies</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📊 Social Darwinism</h4>
                <p className="text-sm text-slate-700 mt-1">A misapplication of Charles Darwin's theory of "natural selection" to human societies. Philosophers like Herbert Spencer argued that white European races were the "fittest" because they were the wealthiest and most powerful, therefore it was "natural" for them to dominate "weaker" races.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">💀 Phrenology</h4>
                <p className="text-sm text-slate-700 mt-1">Pseudoscience involving skull measurements used to claim that African and Indigenous skulls proved mental inferiority.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">✝️ The "Civilizing Mission"</h4>
                <p className="text-sm text-slate-700 mt-1">Based on Rudyard Kipling's poem "The White Man's Burden." The belief that it was the moral duty of white Europeans/Americans to "civilize" non-white peoples by bringing them education, Christianity, and Western culture. Used to justify French control in West Africa and US control in the Philippines.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Nationalism</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Empire building became a competitive sport among European nations. Possessing colonies was a status symbol.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇬🇧 Britain</h4>
                <p className="text-sm text-slate-700 mt-1">Wanted to protect trade routes to India ("The Jewel in the Crown").</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-50 to-teal-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-teal-900">🇫🇷 France</h4>
                <p className="text-sm text-slate-700 mt-1">Sought to restore prestige after losing the Franco-Prussian War (1871).</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-emerald-900">🇯🇵 Japan</h4>
                <p className="text-sm text-slate-700 mt-1">Asserted national pride and resisted Western domination by becoming an imperial power itself (Meiji Restoration).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛪ Religious Motives</h3>
            <p className="text-slate-700 leading-relaxed">
              Missionaries accompanied conquerors to convert indigenous populations to Christianity. While often humanitarian (building schools and hospitals), missionaries also paved the way for political control and undermined local cultures.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-2",
      title: "Topic 6.2: State Expansion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏢 Shift from Private to State Control</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              European states shifted from private trading companies to direct state control, fueling a "land grab" in Africa, Asia, and the Pacific.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">🇧🇪 The Congo</h4>
                <p className="text-sm text-slate-700 mt-1">Originally the private property of <strong>King Leopold II of Belgium</strong>. Leopold forced rubber extraction through brutal mutilation and terror. Public outrage forced the Belgian government to take direct control in 1908 (becoming the Belgian Congo).</p>
              </div>
              <div className="bg-gradient-to-r from-violet-50 to-indigo-50 p-4 rounded-lg border-l-4 border-violet-400">
                <h4 className="font-bold text-indigo-900">🇮🇩 Indonesia</h4>
                <p className="text-sm text-slate-700 mt-1">Shifted from the <strong>Dutch East India Company (VOC)</strong> to direct control by the Dutch government due to corruption and debt.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-blue-900">🇮🇳 India</h4>
                <p className="text-sm text-slate-700 mt-1">Shifted from the <strong>British East India Company (EIC)</strong> to direct rule (<strong>The British Raj</strong>) following the Sepoy Mutiny of 1857.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗺️ The Scramble for Africa</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">📍 Berlin Conference (1884-1885)</h4>
                <p className="text-sm text-slate-700 mt-1">Organized by Otto von Bismarck. European powers drew colonial borders on a map of Africa without regard for ethnic/linguistic groups. Goal: To prevent war between European powers. Result: By 1900, nearly all of Africa was colonized (except Ethiopia and Liberia).</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🏘️ Settler Colonies</h4>
                <p className="text-sm text-slate-700 mt-1">Areas where large numbers of Europeans moved to live permanently. <strong>South Africa:</strong> British conflict with Afrikaners (descendants of Dutch settlers) led to the <strong>Boer Wars</strong> (concentration camps used; British victory). <strong>Australia/New Zealand:</strong> British penal and settler colonies displaced Aborigines and Maori.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌏 Expansion in Asia and the Pacific</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇺🇸 United States</h4>
                <p className="text-sm text-slate-700 mt-1">Expanded influence under Manifest Destiny. Took <strong>Philippines, Guam, Puerto Rico,</strong> and <strong>Cuba</strong> following the Spanish-American War (1898). Annexed <strong>Hawaii</strong> for sugar plantations and naval bases.</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-50 to-teal-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-teal-900">🇯🇵 Japan</h4>
                <p className="text-sm text-slate-700 mt-1">Expanded into Korea, Taiwan, and Manchuria (Sino-Japanese War).</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-emerald-900">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-1">Expanded into Central Asia and clashed with Britain (The "Great Game" for control of Afghanistan).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-3",
      title: "Topic 6.3: Indigenous Responses to State Expansion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ Direct Resistance</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🇵🇪 Túpac Amaru II (Peru)</h4>
                <p className="text-sm text-slate-700 mt-1">Indigenous leader who led a massive peasant rebellion against Spanish control in the 1780s. He was captured and executed.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-yellow-900">🇬🇳 Samory Touré (West Africa)</h4>
                <p className="text-sm text-slate-700 mt-1">Led a warrior state against the French for nearly 20 years.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-amber-900">👑 Yaa Asantewaa (West Africa)</h4>
                <p className="text-sm text-slate-700 mt-1">Queen Mother of the Ashanti Empire; led the "War of the Golden Stool" against the British.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-red-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">🇮🇳 Sepoy Mutiny (India, 1857)</h4>
                <p className="text-sm text-slate-700 mt-1">Indian soldiers (Sepoys) employed by the British rebelled over the use of pig/cow fat on rifle cartridges (offensive to Muslims/Hindus). Resulted in the end of the Mughal Empire and the start of the British Raj.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🙏 Religious/Millenarian Movements</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <em>Millenarian movements often believed that spiritual purity would supernaturally drive out the colonizers.</em>
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">👻 Ghost Dance (USA)</h4>
                <p className="text-sm text-slate-700 mt-1">Native American ritual intended to drive away white settlers and restore the buffalo. Culminated in the Wounded Knee Massacre (1890).</p>
              </div>
              <div className="bg-gradient-to-r from-violet-50 to-indigo-50 p-4 rounded-lg border border-violet-200">
                <h4 className="font-bold text-indigo-900">🐄 Xhosa Cattle Killing Movement (South Africa)</h4>
                <p className="text-sm text-slate-700 mt-1">Xhosa people killed their cattle and destroyed crops, believing spirits would drive the British into the sea. Resulted in massive famine.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-blue-900">☪️ Mahdist Revolt (Sudan)</h4>
                <p className="text-sm text-slate-700 mt-1">Islamic revolt led by Muhammad Ahmad (the Mahdi) against Egyptian/British rule.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏰 New States on the Periphery</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-l-4 border-green-400">
                <h4 className="font-bold text-green-900">🦁 The Zulu Kingdom</h4>
                <p className="text-sm text-slate-700 mt-1">Established by <strong>Shaka Zulu</strong>; offered fierce military resistance to the British/Boers but eventually fell due to British technology.</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border-l-4 border-emerald-400">
                <h4 className="font-bold text-teal-900">🏛️ The Balkans</h4>
                <p className="text-sm text-slate-700 mt-1">Rising nationalism led to rebellion against the Ottoman Empire, establishing independent states like Greece, Serbia, and Bulgaria.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-cyan-900">🕌 Sokoto Caliphate</h4>
                <p className="text-sm text-slate-700 mt-1">Established in West Africa (Nigeria) based on Islamic law; eventually absorbed by the British.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-4",
      title: "Topic 6.4: Global Economic Development",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Export Economies</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Industrial Revolution required two things: Raw Materials and Markets. Colonial economies were reorganized to serve the needs of the metropole (imperial center).
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Cash Crops" 
                definition="Colonies were forced to stop subsistence farming and focus on cash crops or raw material extraction."
              />
              <KeyTerm 
                term="Monocultures" 
                definition="Economies dependent on a single commodity, making them vulnerable to price fluctuations and soil depletion."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Key Resources (Raw Materials)</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🌾 Cotton</h4>
                <p className="text-sm text-slate-700 mt-1">Britain relied on US cotton until the Civil War blocked supply; shifted focus to <strong>Egypt</strong> and <strong>India</strong>.</p>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🌳 Rubber</h4>
                <p className="text-sm text-slate-700 mt-1">Essential for tires/hoses. Extracted from the <strong>Amazon</strong> and the <strong>Congo</strong>.</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🌴 Palm Oil</h4>
                <p className="text-sm text-slate-700 mt-1">Essential for lubricating factory machines. Extracted from <strong>West Africa</strong>.</p>
              </div>
              <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">💩 Guano</h4>
                <p className="text-sm text-slate-700 mt-1">Bat/Bird droppings used as fertilizer. Extracted from <strong>Peru</strong> and <strong>Chile</strong>.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">💎 Diamonds</h4>
                <p className="text-sm text-slate-700 mt-1">Cecil Rhodes (De Beers) monopolized diamond extraction in <strong>South Africa</strong>.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-5",
      title: "Topic 6.5: Economic Imperialism",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💳 Economic Imperialism in Asia</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Foreign control exerted through economic domination rather than direct political rule (Neocolonialism).
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">🇨🇳 The Opium Wars (China)</h4>
                <p className="text-sm text-slate-700 mt-1">Britain had a trade deficit with China (bought tea/silk, China bought nothing). Britain forced <strong>Opium</strong> (from India) into China to correct the balance. <strong>Treaty of Nanking:</strong> China lost the Opium Wars; forced to open ports, pay reparations, and cede Hong Kong. Led to <strong>Spheres of Influence</strong>.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-yellow-900">🇦🇷 Port of Buenos Aires</h4>
                <p className="text-sm text-slate-700 mt-1">British firms financed and built the port, giving Britain control over Argentine trade.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌎 Economic Imperialism in Latin America</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🍌 The United Fruit Company</h4>
                <p className="text-sm text-slate-700 mt-1">An American corporation that traded tropical fruit (bananas) grown on Latin American plantations. They allied with landowners and politicians to keep wages low, creating "Banana Republics."</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-orange-900">⛏️ Mining</h4>
                <p className="text-sm text-slate-700 mt-1">US and British investors controlled copper mining in Chile and Mexico.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-6",
      title: "Topic 6.6: Causes of Migration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📤 Push Factors (Why leave?)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📈 Population Growth</h4>
                <p className="text-sm text-slate-700 mt-1">Improved medicine/food led to crowding.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-yellow-900">🥔 Famine</h4>
                <p className="text-sm text-slate-700 mt-1">The <strong>Irish Potato Famine</strong> (1845-1849) pushed millions of Irish to the US.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-amber-900">⚔️ Political Instability</h4>
                <p className="text-sm text-slate-700 mt-1">Italians and Lebanese left due to poverty and civil unrest.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-red-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">🇨🇳 Chaos in China</h4>
                <p className="text-sm text-slate-700 mt-1">The Taiping Rebellion drove Chinese emigration.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📥 Pull Factors (Why go there?)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🏭 Urbanization</h4>
                <p className="text-sm text-slate-700 mt-1">Jobs in factories (US, Britain).</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-50 to-teal-50 p-4 rounded-lg border border-cyan-200">
                <h4 className="font-bold text-teal-900">🏘️ Settler Colonies</h4>
                <p className="text-sm text-slate-700 mt-1">Free or cheap land in Australia, Canada, US.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-green-50 p-4 rounded-lg border border-teal-200">
                <h4 className="font-bold text-green-900">🌾 Seasonal Labor</h4>
                <p className="text-sm text-slate-700 mt-1">Italians moved to Argentina for harvest, then returned home ("Swallows").</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛓️ New Labor Systems (Coerced Migration)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Slavery was slowly being abolished (Britain 1833, US 1865, Brazil 1888), creating a labor shortage.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">📜 Indentured Servitude</h4>
                <p className="text-sm text-slate-700 mt-1">Workers (mostly Indian and Chinese) signed contracts to work for a set time in exchange for passage. <strong>Indians</strong> → Caribbean, Africa, Fiji (Sugar). <strong>Chinese</strong> → US, Caribbean, South America (Railroads, Guano, Sugar).</p>
              </div>
              <div className="bg-gradient-to-r from-violet-50 to-indigo-50 p-4 rounded-lg border border-violet-200">
                <h4 className="font-bold text-indigo-900">🔒 Convict Labor</h4>
                <p className="text-sm text-slate-700 mt-1">Britain sent convicts to <strong>Australia</strong> (penal colony).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-7",
      title: "Topic 6.7: Effects of Migration",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 Changes in Home Societies</h3>
            <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
              <h4 className="font-bold text-pink-900">👩 Gender Roles</h4>
              <p className="text-sm text-slate-700 mt-1">As men migrated for work, women in home societies (e.g., Africa) took on "male" roles in trade and agriculture, gaining some autonomy but increasing their workload.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏘️ Ethnic Enclaves</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Migrants clustered together, transplanting their culture to new environments.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🏮 Chinatowns</h4>
                <p className="text-sm text-slate-700 mt-1">Developed in San Francisco, Lima, and Southeast Asia.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-yellow-900">🇮🇳 Indians</h4>
                <p className="text-sm text-slate-700 mt-1">Brought Hinduism, curry, and caste concepts to the Caribbean and South Africa (Gandhi started his activism in S. Africa).</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-amber-900">🇮🇹 Italians</h4>
                <p className="text-sm text-slate-700 mt-1">Influenced Argentine culture (language/food).</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-green-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-green-900">🇮🇪 Irish</h4>
                <p className="text-sm text-slate-700 mt-1">Dominated low-wage industrial jobs in the Northeast US.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Prejudice and Regulation of Immigration</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Receiving societies often reacted with Nativism (protecting interests of native-born inhabitants).
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇺🇸 United States</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Chinese Exclusion Act (1882):</strong> First US law to ban a specific ethnic group.</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-50 to-teal-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-teal-900">🇦🇺 Australia</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>White Australia Policy (1901):</strong> Restricted non-white immigration (specifically Chinese) to maintain a "British" identity.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-8",
      title: "Topic 6.8: Causation in the Imperial Age",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 Key Causal Chains</h3>
            <ImportantNote>
              The Industrial Revolution was the primary engine of 19th-century history. It provided the <strong>means</strong> (steamboats, maxim guns, quinine) and the <strong>motive</strong> (markets, raw materials) for Imperialism.
            </ImportantNote>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">⚙️ Tech → Expansion</h4>
                <p className="text-sm text-slate-700 mt-1">Quinine (anti-malaria) allowed Europeans to enter the African interior; steamships allowed upstream travel; telegraphs allowed administration of distant colonies.</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-50 to-teal-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-teal-900">💰 Economics → Migration</h4>
                <p className="text-sm text-slate-700 mt-1">Demand for raw materials led to plantation economies, which required labor (indentured servitude) after the abolition of slavery, causing massive Indian/Chinese migration.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-emerald-900">✊ Expansion → Resistance</h4>
                <p className="text-sm text-slate-700 mt-1">The encroachment of Western powers sparked nationalist and anti-colonial movements (Sepoy Mutiny, Ghost Dance) that laid the groundwork for 20th-century decolonization.</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-4 rounded-lg border-l-4 border-emerald-400">
                <h4 className="font-bold text-green-900">🌍 Imperialism → Global Economy</h4>
                <p className="text-sm text-slate-700 mt-1">The world became integrated into a single market, but it was unequal; the "Global South" produced resources, and the "Global North" produced finished goods and accumulated capital.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default worldHistoryUnit6Notes;